import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  deleteReport,
  updateReport,
} from 'features/reports/hooks/useReport/useReport.queries';
import { UseReportMutations } from 'features/reports/hooks/useReport/useReport.types';
import { Report } from 'features/reports/report.types';

export const useReportMutations = (reportId: number): UseReportMutations => {
  const queryClient = useQueryClient();

  const invalidateQueries = async (): Promise<[void, void, void]> =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['report', reportId] }),
      queryClient.invalidateQueries({ queryKey: ['moderationReports'] }),
      queryClient.invalidateQueries({ queryKey: ['reports', 'photos'] }),
    ]);

  const deleteMutation = useMutation({
    mutationFn: deleteReport,
    onSettled: invalidateQueries,
  });

  const updateMutation = useMutation({
    mutationFn: updateReport,
    onSettled: invalidateQueries,
  });

  const onDeleteReport = async (rejectDevice?: boolean): Promise<void> => {
    await deleteMutation.mutateAsync({ id: reportId, rejectDevice });
  };

  const onUpdateReport = async (params: Partial<Report>): Promise<Report> => {
    const resp = await updateMutation.mutateAsync({ id: reportId, ...params });
    return resp;
  };

  return {
    isReportUpdating: updateMutation.isPending || deleteMutation.isPending,
    onDeleteReport,
    onUpdateReport,
  };
};
